import React, { useEffect, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import './StorefrontBookingCalendar.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function StorefrontBookingCalendar({
  value,
  onChange,
  minDate,
  maxDate,
  isDateUnavailable,
  availabilityLoading = false,
  onMonthChange,
}) {
  const min = startOfDay(minDate);
  const max = startOfDay(maxDate);

  const [monthCursor, setMonthCursor] = useState(() =>
    startOfMonth(value ? startOfDay(value) : min)
  );

  useEffect(() => {
    if (value) {
      setMonthCursor(startOfMonth(startOfDay(value)));
    }
  }, [value]);

  useEffect(() => {
    onMonthChange?.(monthCursor);
  }, [monthCursor, onMonthChange]);

  const monthStart = startOfMonth(monthCursor);
  const monthEnd = endOfMonth(monthCursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const prevMonthEnd = endOfMonth(subMonths(monthCursor, 1));
  const canGoPrev = !isBefore(prevMonthEnd, min);
  const nextMonthStart = startOfMonth(addMonths(monthCursor, 1));
  const canGoNext = !isAfter(nextMonthStart, max);

  const isOutOfRange = (d) => {
    const x = startOfDay(d);
    return isBefore(x, min) || isAfter(x, max);
  };

  const todayStart = startOfDay(new Date());

  return (
    <div
      className={`storefront-booking-calendar${availabilityLoading ? ' storefront-booking-calendar--loading' : ''}`}
      role="application"
      aria-label="Calendar date picker"
      aria-busy={availabilityLoading}
    >
      <div className="storefront-booking-calendar__header">
        <button
          type="button"
          className="storefront-booking-calendar__nav"
          onClick={() => setMonthCursor((m) => subMonths(m, 1))}
          disabled={!canGoPrev}
          aria-label="Previous month"
        >
          ‹
        </button>
        <div className="storefront-booking-calendar__title" aria-live="polite">
          {format(monthCursor, 'MMMM yyyy')}
        </div>
        <button
          type="button"
          className="storefront-booking-calendar__nav"
          onClick={() => setMonthCursor((m) => addMonths(m, 1))}
          disabled={!canGoNext}
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className="storefront-booking-calendar__weekdays">
        {WEEKDAYS.map((d) => (
          <div key={d} className="storefront-booking-calendar__weekday">
            {d}
          </div>
        ))}
      </div>
      <div className="storefront-booking-calendar__grid">
        {days.map((day) => {
          const outside = !isSameMonth(day, monthCursor);
          const outOfRange = isOutOfRange(day);
          const unavailable = !outside && !outOfRange && Boolean(isDateUnavailable?.(day));
          const disabled = outOfRange || outside || unavailable;
          const selected = Boolean(value && isSameDay(startOfDay(day), startOfDay(value)));
          const isToday = isSameDay(startOfDay(day), todayStart);
          const emphasizeToday =
            isToday && (!value || isSameDay(startOfDay(value), todayStart));
          const showTodayMarker =
            isToday && !emphasizeToday && !outside && !disabled;

          return (
            <button
              key={day.toISOString()}
              type="button"
              className={[
                'storefront-booking-calendar__day',
                outside && 'storefront-booking-calendar__day--outside',
                outOfRange && 'storefront-booking-calendar__day--disabled',
                unavailable && 'storefront-booking-calendar__day--unavailable',
                emphasizeToday && !selected && !unavailable && 'storefront-booking-calendar__day--today',
                showTodayMarker && 'storefront-booking-calendar__day--today-marker',
                selected && 'storefront-booking-calendar__day--selected',
              ]
                .filter(Boolean)
                .join(' ')}
              disabled={disabled}
              onClick={() => onChange(startOfDay(day))}
              aria-label={
                unavailable
                  ? `${format(day, 'EEEE, MMMM d, yyyy')} — no times available`
                  : format(day, 'EEEE, MMMM d, yyyy')
              }
              aria-current={isToday ? 'date' : undefined}
              aria-pressed={selected}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}
